import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { Region } from '../../../core/models/region';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RegionService } from '../../../core/services/region.service';
import { RegionFormComponent } from './region-form/region-form.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';


@Component({
  selector: 'em-region',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, ToastModule, ConfirmDialogModule, TranslatePipe, EmPermissionDirective],
  templateUrl: './region.component.html',
  styleUrl: './region.component.scss',
  providers: [ConfirmationService],
})
export class RegionComponent {
  regions!: Region[];
  ref: DynamicDialogRef | undefined;
  translations: any = {};
  Permission = Permission;

  constructor(
    private regionService: RegionService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private translate: TranslateService,
  ) {}

  ngOnInit() {
    this.getAllRegions();
    this.loadTranslations();
  }

  loadTranslations() {
    this.translate
      .get([
        'layout.configurations.regions.title',
        'layout.configurations.regions.name',
        'layout.configurations.regions.code',
        'layout.configurations.regions.actions',
        'layout.configurations.regions.addRegion.title',
        'layout.configurations.regions.editRegion.title',
        'layout.configurations.regions.deleteRegion.title',
        'layout.configurations.regions.deleteRegion.detail',
        'layout.configurations.regions.deleteRegion.delete',
        'layout.configurations.regions.deleteRegion.cancel',
        'layout.configurations.regions.deleteRegion.successMessage.title',
        'layout.configurations.regions.deleteRegion.successMessage.detail',
        'layout.configurations.regions.editRegion.successMessage.title',
        'layout.configurations.regions.editRegion.successMessage.detail',
        'layout.configurations.regions.addRegion.successMessage.title',
        'layout.configurations.regions.addRegion.successMessage.detail',
        'layout.configurations.regions.deleteRegion.errorMessage.title',
        'layout.configurations.regions.deleteRegion.errorMessage.detail',
        'layout.configurations.regions.deleteRegion.infoMessage.title',
        'layout.configurations.regions.deleteRegion.infoMessage.detail',
        'layout.configurations.regions.deleteRegion.cancelDeletion.title',
        'layout.configurations.regions.deleteRegion.cancelDeletion.message',
      ])
      .subscribe((translations) => {
        this.translations = translations;
      });
  }

  getAllRegions() {
    this.regionService.getRegions().subscribe((data) => (this.regions = data));
  }

  onAdd() {
    this.ref = this.dialogService.open(RegionFormComponent, {
      header: this.translations['layout.configurations.regions.addRegion.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
    });

    this.ref.onClose.subscribe((path) => {
      if (path) {
        this.getAllRegions();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.regions.addRegion.successMessage.title'],
          detail: this.translations['layout.configurations.regions.addRegion.successMessage.detail'],
        });
      }
    });
  }

  onEdit(region: Region) {
    this.ref = this.dialogService.open(RegionFormComponent, {
      header: this.translations['layout.configurations.regions.editRegion.title'],
      width: '320px',
      contentStyle: { overflow: 'auto' },
      data: region,
    });

    this.ref.onClose.subscribe((path) => {
      if (path) {
        this.getAllRegions();
        if (path.war) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('layout.configurations.regions.editRegion.warningMessage.title'),
            detail: this.translate.instant(
              'layout.configurations.regions.editRegion.warningMessage.changeFieldsToUpdate',
            ),
          });
        }else if(path.succes){
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.regions.editRegion.successMessage.title'],
          detail: this.translations['layout.configurations.regions.editRegion.successMessage.detail'],
        });
        }
      }
    });
  }

  onDelete(event: Event, id: number) {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.translations['layout.configurations.regions.deleteRegion.detail'],
      icon: 'pi pi-exclamation-triangle',
      header: this.translations['layout.configurations.regions.deleteRegion.title'],
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translations['layout.configurations.regions.deleteRegion.delete'],
      rejectLabel: this.translations['layout.configurations.regions.deleteRegion.cancel'],
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.deleteRegion(id);
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translations['layout.configurations.regions.deleteRegion.cancelDeletion.title'],
          detail: this.translations['layout.configurations.regions.deleteRegion.cancelDeletion.message'],
          life: 3000,
        });
      },
    });
  }

  deleteRegion(id: number) {
    this.regionService.deleteRegion(id).subscribe({
      next: () => {
        this.getAllRegions();
        this.messageService.add({
          severity: 'success',
          summary: this.translations['layout.configurations.regions.deleteRegion.successMessage.title'],
          detail: this.translations['layout.configurations.regions.deleteRegion.successMessage.detail'],
        });
      },
      error: (err) => {
        console.log('Error deleting region: ' + err);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.regions.deleteRegion.errorMessage.title'],
          detail: this.translations['layout.configurations.regions.deleteRegion.errorMessage.detail'],
        });
      },
    });
  }
}
